import { Fragment, useMemo } from 'react';
import { CircleHelp, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Combobox,
  ComboboxCollection,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxInput,
  ComboboxItem,
  ComboboxLabel,
  ComboboxList,
  ComboboxSeparator,
} from '@/components/ui/combobox';
import { Field, FieldDescription, FieldLabel } from '@/components/ui/field';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { type TemplatePlaceholder } from './eventNameTemplatePreview';
import { useAorText } from 'utils/aorTerminology';
import type { OrgEventAutomationSource } from 'hooks/orgEvents/orgEventAutomationHooks';
import {
  dataFeedGroups,
  dataFeedKeyForSource,
  dataFeedOptionMatches,
  readableFeedSource,
  type DataFeedPick,
} from './dataFeedOptions';
import { CRITERION_HELP, type SelectOption } from './ruleFormModel';
import { useExternalFeedCatalog } from 'hooks/externalFeeds/useExternalFeedCatalog';
import { isFeedUri } from './externalFeeds';

export function CriterionHelpIcon({
  includeAttribute = true,
}: {
  includeAttribute?: boolean;
}) {
  const aorText = useAorText();
  const entries = includeAttribute
    ? CRITERION_HELP
    : CRITERION_HELP.filter((entry) => entry.kind !== 'attribute');
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <button
            type="button"
            aria-label="What do condition types mean?"
            className="tw:inline-flex tw:cursor-help tw:text-muted-foreground tw:hover:text-foreground"
          />
        }
      >
        <CircleHelp className="tw:h-3.5 tw:w-3.5" />
      </TooltipTrigger>
      <TooltipContent className="tw:max-w-[360px]">
        <ul className="tw:m-0 tw:space-y-1.5 tw:pl-0">
          {entries.map((entry) => (
            <li key={entry.kind} className="tw:list-none">
              <span className="tw:font-semibold">{aorText(entry.label)}:</span>{' '}
              {aorText(entry.help)}
            </li>
          ))}
        </ul>
      </TooltipContent>
    </Tooltip>
  );
}

export function HelpIcon({ label, text }: { label: string; text: string }) {
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <button
            type="button"
            aria-label={label}
            className="tw:inline-flex tw:cursor-help tw:text-muted-foreground tw:hover:text-foreground"
          />
        }
      >
        <CircleHelp className="tw:h-3.5 tw:w-3.5" />
      </TooltipTrigger>
      <TooltipContent className="tw:max-w-[280px]">{text}</TooltipContent>
    </Tooltip>
  );
}

export function SimpleSelect({
  value,
  onChange,
  options,
  placeholder,
  className,
}: {
  value: string;
  onChange: (next: string) => void;
  options: SelectOption[];
  placeholder?: string;
  className?: string;
}) {
  const aorText = useAorText();
  const selectedLabel = aorText(
    options.find((opt) => opt.value === value)?.label ?? placeholder ?? value
  );

  return (
    <Select value={value} onValueChange={(next) => onChange(String(next))}>
      <SelectTrigger size="sm" className={className ?? 'tw:w-full'}>
        <SelectValue placeholder={placeholder}>{selectedLabel}</SelectValue>
      </SelectTrigger>
      <SelectContent className="dark">
        {options.map((opt) => (
          <SelectItem key={opt.value} value={opt.value}>
            {aorText(opt.label)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/**
 * The single Data feed control: built-in feeds and every catalog layer, each
 * shown with its name and readable source.
 */
export function DataFeedField({
  source,
  onPick,
  className,
}: {
  source: OrgEventAutomationSource | undefined;
  onPick: (pick: DataFeedPick) => void;
  className?: string;
}) {
  const { data: externalFeeds } = useExternalFeedCatalog();
  const groups = useMemo(() => dataFeedGroups(externalFeeds), [externalFeeds]);
  const optionByKey = useMemo(
    () =>
      new Map(
        groups.flatMap((group) =>
          group.items.map((option) => [option.key, option] as const)
        )
      ),
    [groups]
  );
  const itemGroups = useMemo(
    () =>
      groups.map((group) => ({
        label: group.label,
        items: group.items.map((option) => option.key),
      })),
    [groups]
  );
  const selectedKey = dataFeedKeyForSource(source);
  const selected = selectedKey ? optionByKey.get(selectedKey) : undefined;
  // Rules saved before free-text entry was removed keep their URL.
  const legacyUrl = selected ? '' : source?.serviceUrl?.trim() || '';

  return (
    <Field className={className}>
      <FieldLabel>Data feed</FieldLabel>
      <Combobox
        items={itemGroups}
        value={selectedKey}
        onValueChange={(next) => {
          const option =
            typeof next === 'string' ? optionByKey.get(next) : undefined;
          if (option) onPick(option.pick);
        }}
        itemToStringLabel={(item: string) => optionByKey.get(item)?.name ?? ''}
        itemToStringValue={(item: string) => item}
        filter={(item: string, query: string) => {
          const option = optionByKey.get(item);
          return option ? dataFeedOptionMatches(option, query) : false;
        }}
      >
        <ComboboxInput
          placeholder={source?.layerLabel || 'Search feeds…'}
          className="tw:w-full"
        />
        <ComboboxContent className="dark">
          <ComboboxEmpty>No feeds found.</ComboboxEmpty>
          <ComboboxList>
            {(group: { label: string; items: string[] }, index: number) => (
              <Fragment key={group.label}>
                {index > 0 ? <ComboboxSeparator /> : null}
                <ComboboxGroup items={group.items}>
                  <ComboboxLabel>{group.label}</ComboboxLabel>
                  <ComboboxCollection>
                    {(item: string) => {
                      const option = optionByKey.get(item);
                      return (
                        <ComboboxItem
                          key={item}
                          value={item}
                          title={option?.url}
                          className="tw:h-auto tw:min-h-8 tw:py-1.5"
                        >
                          <span className="tw:flex tw:min-w-0 tw:flex-col tw:gap-0.5">
                            <span className="tw:leading-snug">
                              {option?.name}
                            </span>
                            <span className="tw:truncate tw:text-xs tw:leading-snug tw:text-muted-foreground">
                              {option?.source}
                            </span>
                          </span>
                        </ComboboxItem>
                      );
                    }}
                  </ComboboxCollection>
                </ComboboxGroup>
              </Fragment>
            )}
          </ComboboxList>
        </ComboboxContent>
      </Combobox>
      {selected || legacyUrl ? (
        <FieldDescription
          className="tw:truncate tw:text-xs"
          title={selected?.url ?? legacyUrl}
        >
          {isFeedUri(source?.serviceUrl)
            ? readableFeedSource(source!.serviceUrl)
            : (selected?.source ?? readableFeedSource(legacyUrl))}
        </FieldDescription>
      ) : (
        <FieldDescription>
          Pick a built-in feed or a catalog layer.
        </FieldDescription>
      )}
    </Field>
  );
}

export function TemplateVariableChips({
  placeholders,
  onInsert,
}: {
  placeholders: TemplatePlaceholder[];
  onInsert: (token: string) => void;
}) {
  const aorText = useAorText();
  return (
    <div className="tw:flex tw:flex-wrap tw:gap-1">
      {placeholders.map((placeholder) => (
        <div
          key={placeholder.token}
          className="tw:flex tw:items-center tw:gap-0.5"
        >
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="tw:h-6 tw:px-1.5 tw:font-mono tw:text-[10px]"
            onClick={() => onInsert(placeholder.token)}
          >
            {`@${placeholder.token}`}
          </Button>
          <Tooltip>
            <TooltipTrigger
              render={
                <button
                  type="button"
                  aria-label={`What @${placeholder.token} contains`}
                  className="tw:inline-flex tw:cursor-help tw:text-muted-foreground tw:hover:text-foreground"
                />
              }
            >
              <Info className="tw:h-3 tw:w-3" />
            </TooltipTrigger>
            <TooltipContent className="tw:max-w-[280px]">
              {aorText(placeholder.description)}
            </TooltipContent>
          </Tooltip>
        </div>
      ))}
    </div>
  );
}
export function CheckboxRow({
  checked,
  onChange,
  label,
  description,
  locked = false,
  tooltip,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
  description?: string;
  locked?: boolean;
  tooltip?: string;
}) {
  const row = (
    <div
      className={`tw:flex tw:items-start tw:gap-2${
        locked ? 'tw:cursor-not-allowed tw:opacity-60' : ''
      }`}
    >
      <Checkbox
        checked={locked ? false : checked}
        disabled={locked}
        onCheckedChange={(next) => {
          if (locked) return;
          onChange(next === true);
        }}
        className="tw:mt-0.5"
      />
      <div className="tw:min-w-0 tw:flex-1">
        <Label className="tw:text-sm tw:font-medium">{label}</Label>
        {description ? (
          <p className="tw:m-0 tw:text-xs tw:text-muted-foreground">
            {description}
          </p>
        ) : null}
      </div>
    </div>
  );
  if (!locked) return row;
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger
          render={<span className="tw:block tw:cursor-not-allowed" />}
        >
          {row}
        </TooltipTrigger>
        <TooltipContent>{tooltip}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/** On/off setting. Prefer over CheckboxRow for anything that toggles state. */
export function ToggleRow({
  checked,
  onChange,
  label,
  description,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
  description?: string;
}) {
  return (
    <div className="tw:flex tw:items-start tw:gap-2.5">
      <Switch
        checked={checked}
        onCheckedChange={(next) => onChange(next === true)}
        className="tw:mt-0.5"
      />
      <div className="tw:min-w-0 tw:flex-1">
        <Label className="tw:text-sm tw:font-medium">{label}</Label>
        {description ? (
          <p className="tw:m-0 tw:text-xs tw:text-muted-foreground">
            {description}
          </p>
        ) : null}
      </div>
    </div>
  );
}
